/**
 * File: src/app/performance-reports/performance-reports.component.ts
 * Author: Wayne Yamashita
 * Date: 2025-03-12
 *
 * Description:
 * Server-side ag-Grid for performance reporting with date-range filtering,
 * POD/BOL audit flags, delivery status, and CSV/Excel export. Accepts
 * query-parameter deep links for pickup or delivery date ranges and formats
 * date fields for display.
 *
 * Responsibilities:
 * Configure a serverSide datasource to /api/performance_reports/performance_reports.php
 * with paging/sort/filter payloads; normalize filter models (text, set, date,
 * number) into a serialized `filters` array; hydrate filters from route params
 * (pickup_from/to, delivery_from/to); expose export helpers that rebind a
 * full-range datasource; provide navigation shortcuts to related reports.
 *
 * Notes:
 * Date columns use valueGetters to emit YYYY-MM-DD strings; `formatAuditedCell`
 * renders ✅/❌ for audit flags. `TRANSACTION_TOTAL_AMOUNT` logic is not used here;
 * totals are derived on the backend and rowCount is returned with the data.
 */


import { Component, OnInit, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GridApi, GridOptions, IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-community';
import 'ag-grid-enterprise';
import { catchError, filter, map, of } from 'rxjs';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { faThemeisle } from '@fortawesome/free-brands-svg-icons';

@Component({
  selector: 'app-performance-reports',
  templateUrl: './performance-reports.component.html',
  styleUrls: ['./performance-reports.component.scss']
})
export class PerformanceReportsComponent implements OnInit {
  @ViewChild('agGrid', { static: false }) agGrid!: AgGridAngular;
  private apiUrl = '/api/performance_reports/performance_reports.php';

  gridApi!: GridApi;
  public gridOptions: GridOptions = {};
  public rowData: any[] = [];
  public totalRows = 0;

  reports_form: FormGroup = this.fb.group({
    pickup_from: [''],
    pickup_to: [''],
    delivery_from: [''],
    delivery_to: [''],
  })

  constructor(private http: HttpClient, private fb: FormBuilder, private route: ActivatedRoute, private router: Router) {
    this.gridOptions = {
      columnDefs: [
        {
          headerName: 'Order Number',
          field: 'alternate_reference',
          cellStyle: {'font-weight': 'bold'},          
          pinned: 'left',
          width: 125
        },
        {
          headerName: 'Pick-up Date', field: 'load_start',
          filter: 'agDateColumnFilter', // Use the date filter type
          valueGetter: params => {
            // Extract the date part
            console.log('params data: ', params)
            const datetime = new Date(params.data.load_start);
            return datetime.toISOString().split('T')[0];
          },
          width: 125
        },
        {
          headerName: 'Delivery Date', field: 'load_end',
          filter: 'agDateColumnFilter', // Use the date filter type
          valueGetter: params => {
            // Extract the date part
            console.log('params data: ', params)
            const datetime = new Date(params.data.load_end);
            return datetime.toISOString().split('T')[0];
          },
          width: 125
        },
        { headerName: 'POD Audited', field: 'pod_audit', valueFormatter: this.formatAuditedCell, width: 125 },        
        { headerName: 'BOL Audited', field: 'bol_audit', valueFormatter: this.formatAuditedCell, width: 125 },      
        {
          headerName: 'Delivery Status',
          field: 'delivery_status',
          width: 200
        },        
      ],
      rowModelType: 'serverSide',
      cacheBlockSize: 20,
      suppressCsvExport: false,
      suppressExcelExport: false,
      onGridReady: this.onGridReady.bind(this)
    };
  }

  defaultColDef = {
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      caseSensitive: false,
    },
    autoHeight: true,
    resizable: true,
  };

  ngOnInit(): void {
    this.gridApi.showLoadingOverlay();
  }

  getServerSideDatasource(): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        this.getPerformanceReports(params.request.startRow, params.request.endRow, params.request.filterModel, params.request.sortModel)
          .subscribe(
            response => {
              console.log('print out the response: ', response)
              if (response) {
                params.success({ rowData: response.rowData, rowCount: response.rowCount });
              } else {
                params.fail();
              }
            },
            error => {
              console.log(error);
              params.fail();
            }
          );
      }
    };
  }
  getPerformanceReports(startRow: number = 0, endRow: number = 0, filterModel: any = {}, sortModel: any = []) {
    const apiUrl = this.apiUrl;
    const filters = Object.entries(filterModel).map(([column, filter]: [string, any]) => {

      let operator = '';
      let value = [];
      console.log('filter model', filterModel)
      if (filter.filterType === 'text') {
        operator = filter.type;
        value = filter.filter;
      } else if (filter.filterType === 'set') {
        operator = 'equals'; // Or any other appropriate operator for set filter
        value = filter.values; // Join selected values with a delimiter if needed
      } else if (filter.filterType === 'date') {
        if (filter.type == 'inRange') {
          operator = filter.type;
          value = [filter.dateFrom, filter.dateTo];
        }
        else {
          operator = 'equals';
          value = filter.dateFrom;
        }

      } else if (filter.filterType === 'number') {
        if (filter.type == 'inRange') {
          operator = filter.type;
          value = [filter.filter, filter.filterTo];
        }
        else {
          operator = 'equals';
          value = filter.filter;
        }
      }

      return {
        column,
        operator,
        value,
      };
    });
    const params: any = {
      startRow: startRow,
      endRow: endRow,
      sortModel: JSON.stringify(sortModel),
      filters: JSON.stringify(filters),
    };
    console.log('an array of filter object: ', filters)
    return this.http.get<any>(apiUrl, { params }).pipe(
      map((response) => {
        console.log('Message from the backend: ', response);
        const newData = response.data.data.map((item: any) => {
          // Convert the 'week_no' property to a number
          const week_no = parseInt(item.week_no, 10);

          // Return a new object with the converted 'week_no'
          return { ...item, week_no };
        });
        return {
          rowData: newData,
          rowCount: response.total,
        };
      }),
      catchError((error) => {
        console.log(error);
        return of(null);
      }),
      filter(response => response !== null)
    );
  }

  createDatasource(): any {
    return {
      getRows: (params: any) => {
        const startRow = params.request.startRow;
        const endRow = params.request.endRow;

        let filters = Object.entries(params.request.filterModel).map(([column, filter]: [string, any]) => {
          let operator = filter.type;
          let value = filter.filter;
          console.log(filter.filter)
          return {
            column,
            operator,
            value
          }
        });
        let sortModel = params.request.sortModel;



        const url = `${this.apiUrl}?startRow=${startRow}&endRow=${endRow}&filters=${JSON.stringify(filters)}&sortModel=${JSON.stringify(sortModel)}`;
        this.http.get<any>(url, {
          params: {
            startRow: startRow,
            endRow: endRow,
            sortModel: JSON.stringify(sortModel),
            filters: JSON.stringify(filters),
          }
        }).subscribe(
          (data) => {
            params.successCallback(data.data, data.totalRows);
          },
          (error) => {
            console.error('Error fetching rows:', error);
            params.failCallback();
          }
        );
      }
    };
  }
  getAllServerSideDatasource(type: string): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        this.getPerformanceReports(0, 999999, params.request.filterModel, params.request.sortModel)
          .subscribe(
            response => {
              if (response) {
                console.log('this is the response: ', response)
                params.success({ rowData: response.rowData, rowCount: response.rowCount });
                if(type == 'csv') {
                  this.gridApi.exportDataAsCsv({
                    fileName: 'Performance_Report', // Change to the desired file name 
                  });  
                }
                else {
                  this.gridApi.exportDataAsExcel({
                    fileName: 'Performance_Report', // Change to the desired file name 
                    sheetName: 'ulaap', // Change to the desired tab name 
                  });
                }
                
              } else {
                params.fail();
              }
            },
            error => {
              console.log(error);
              params.fail();
            }
          );
      }
    };
  }
  exportToCSV() {
    this.gridApi.setGridOption('serverSideDatasource',this.getAllServerSideDatasource('csv'));
    
  }

  exportToExcel() {
    this.gridApi.setGridOption('serverSideDatasource',this.getAllServerSideDatasource('excel'));
  }

  //Apply filter dynmically
  ngAfterViewInit() {
    if (this.agGrid) {
      this.route.queryParamMap.subscribe((params) => {
        const pickupFrom = params.get('pickup_from');
        const pickupTo = params.get('pickup_to');
        const deliveryFrom = params.get('delivery_from');
        const deliveryTo = params.get('delivery_to');
        console.log('ngafterviewinit triggered')
        if (pickupFrom && pickupTo) {
          this.applyPickupFilter(pickupFrom, pickupTo);
        }
        else if (deliveryFrom && deliveryTo) {
          this.applyDeliveryFilter(deliveryFrom, deliveryTo);
        }
        else {
          this.agGrid.api.setFilterModel({});
        }
      });
    }
  }

  //Apply filter for pickup date
  applyPickupFilter(filterText: string, filterText2: string) {
    if (this.agGrid) {
      this.agGrid.api.setFilterModel({
        load_start: {
          dateFrom: filterText,
          dateTo: filterText2,
          type: 'inRange',
        },
      });
      this.agGrid.api.onFilterChanged();
    }
  }

  //Apply filter for delivery date
  applyDeliveryFilter(filterText: string, filterText2: string) {
    if (this.agGrid) {
      console.log(filterText, filterText2)
      this.agGrid.api.setFilterModel({
        load_end: {
          dateFrom: filterText,
          dateTo: filterText2,
          type: 'inRange',
        },
      });
      this.agGrid.api.onFilterChanged();
    }
  }


  formatDateAsYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  search() {
    if (this.reports_form.get('pickup_from')?.value) {
      const pickup_from = this.formatDateAsYYYYMMDD(this.reports_form.get('pickup_from')?.value)
      const pickup_to = this.formatDateAsYYYYMMDD(this.reports_form.get('pickup_to')?.value)
      this.router.navigate(['/delivery_reports'], {
        queryParams: {
          pickup_from: pickup_from,
          pickup_to: pickup_to
        }
      });
    }
    else if (this.reports_form.get('delivery_from')?.value) {
      const delivery_from = this.formatDateAsYYYYMMDD(this.reports_form.get('delivery_from')?.value)
      const delivery_to = this.formatDateAsYYYYMMDD(this.reports_form.get('delivery_to')?.value)
      this.router.navigate(['/delivery_reports'], {
        queryParams: {
          delivery_from: delivery_from,
          delivery_to: delivery_to
        }
      });
    }
    else {
      this.router.navigate(['/delivery_reports'], {
        queryParams: {
          week_from: this.reports_form.get('week_from')?.value,
          week_to: this.reports_form.get('week_to')?.value
        }
      });
    }
  }

  //Reset the filter by setting to empty object
  resetFilter() {
    this.reports_form.get('pickup_from')?.setValue('');
    this.reports_form.get('pickup_to')?.setValue('');
    this.reports_form.get('delivery_from')?.setValue('');
    this.reports_form.get('delivery_to')?.setValue('');
    this.router.navigate(['/performance_reports']);
  }

  //Whenever the toggle button value got changed, we reset the form
  onToggleButtonChange(event: any) {
    if (event.value == 'pickup') {
      this.reports_form.get('delivery_from')?.setValue('');
      this.reports_form.get('delivery_to')?.setValue('');
    }
    else if (event.value == 'delivery') {
      this.reports_form.get('pickup_from')?.setValue('');
      this.reports_form.get('pickup_to')?.setValue('');
    }
  }

  onGridReady(params: any): void {
    this.gridApi = params.api;
    this.gridApi.setGridOption('serverSideDatasource',this.getServerSideDatasource());
  }
  
  formatAuditedCell(params: any): string {
    const delivered = params.value;
    return delivered ? '✅' : '❌' ;
  }
  formatDeliveredCell(params: any): string {
    const delivered = params.value;
    return delivered ? 'Yes' : 'No';
  }
  storeReports() {
    this.router.navigate(['/store_reports']);
  }

  exceptionReports() {
    this.router.navigate(['/exception_reports']);
  }


  deliveryReports() {
    this.router.navigate(['/delivery_reports']);
  }  
}